import React, { ReactElement, ReactNode } from 'react';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import { useAmp } from 'next/amp';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import { DefaultSeoProps } from 'next-seo';
import createEmotionCache from '@mais-retorno-front/theme/createEmotionCache';
import type { ThemeProviderProps as EmotionThemeProviderProps } from '@emotion/react';
import { StyledEngineProvider } from '@mui/material';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './styles.css';
import '@mais-retorno-front/configs/dayjs';
import { GoogleTagManager } from '@next/third-parties/google';

import {
  MR_SCHEMA,
  OPEN_GRAPH_IMAGE,
} from '@mais-retorno-front/constants/mais-retorno-description';
import theme from '@mais-retorno-front/theme/theme';
import { SwrProvider } from '@mais-retorno-front/providers/swr.provider';
import { useSaveUTMParams } from '@mais-retorno-front/components/utm-button/helper';

const GetSiteControlConfig = dynamic(() =>
  import('@mais-retorno-front/components/get-site-control-config').then(
    (mod) => mod.GetSiteControlConfig
  )
);

const AuthProvider = dynamic<any>(() =>
  import('@core/features-core/auth/client').then((mod) => mod.AuthProvider)
);

const TrackingProvider = dynamic<any>(() =>
  import('@mais-retorno-front/context/tracking-events').then(
    (mod) => mod.TrackingEventsProvider
  )
);

const CacheProvider = dynamic<any>(
  import('@emotion/react').then((m) => m.CacheProvider)
);

const EmotionThemeProvider = dynamic<EmotionThemeProviderProps>(
  import('@emotion/react').then((m) => m.ThemeProvider)
);

const DefaultSeo = dynamic<DefaultSeoProps>(
  import('next-seo').then((mod) => mod.DefaultSeo)
);

const CssBaseline = dynamic(() => import('@mui/material/CssBaseline'));

const MuiThemeProvider = dynamic<any>(() =>
  import('@mui/material/styles').then((mod) => mod.ThemeProvider)
);

const BreadcrumbsProvider = dynamic(() =>
  import('@mais-retorno-front/context/breadcrumbs').then(
    (mod) => mod.BreadcrumbsProvider
  )
);

const MuiPickersUtilsProvider = dynamic<any>(() =>
  import('@mais-retorno-front/providers/muiPickersUltils.provider').then(
    (mod) => mod.MuiPickersUtilsProvider
  )
);

const RetornoAdsProvider = dynamic(() =>
  import('@mais-retorno-front/components/retorno-ads/context').then(
    (mod) => mod.RetornoAdsProvider
  )
);

const SessionProvider = dynamic<any>(() =>
  import('next-auth/react').then((mod) => mod.SessionProvider)
);

const SnackbarProvider = dynamic(() =>
  import('@core/design-system/snackbar').then((m) => m.SnackbarProvider)
);

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  emotionCache: any;
};

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

function MyApp({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}: AppPropsWithLayout) {
  const isAmp = useAmp();
  useSaveUTMParams();

  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <StyledEngineProvider injectFirst>
      <CacheProvider value={emotionCache}>
        <Script
          strategy="afterInteractive"
          id="hotjar"
          dangerouslySetInnerHTML={{
            __html: `
          (function(h,o,t,j,a,r){
            h.hj = h.hj || function(){(h.hj.q = h.hj.q || []).push(arguments);};
            h._hjSettings = {hjid: 3840862, hjsv: 6};
            a = o.getElementsByTagName('head')[0];
            r = o.createElement('script'); r.async = 1;
            r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
            a.appendChild(r);
          })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
        `,
          }}
        />
        <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_TAG_MANAGER} />

        <MuiThemeProvider theme={theme}>
          <EmotionThemeProvider theme={theme}>
            <CssBaseline />
            <DefaultSeo
              openGraph={{
                type: 'website',
                url: 'https://maisretorno.com/',
                title: MR_SCHEMA.organizationTitle,
                description: MR_SCHEMA.organizationDescription,
                site_name: MR_SCHEMA.organizationName,
                locale: MR_SCHEMA.organizationLocale,
                images: [OPEN_GRAPH_IMAGE],
              }}
            />

            <TrackingProvider>
              <MuiPickersUtilsProvider>
                <SessionProvider session={(pageProps as any).session}>
                  <RetornoAdsProvider>
                    <AuthProvider>
                      <SwrProvider>
                        <BreadcrumbsProvider>
                          {isAmp && (
                            <amp-analytics
                              config={`https://www.googletagmanager.com/amp.json?id=${process.env.NEXT_PUBLIC_AMP_TAG_MANAGER}&gtm.url=SOURCE_URL`}
                              data-credentials="include"
                            ></amp-analytics>
                          )}
                          <SnackbarProvider>
                            {getLayout(<Component {...pageProps} />)}
                          </SnackbarProvider>
                        </BreadcrumbsProvider>
                        <GetSiteControlConfig />
                      </SwrProvider>
                    </AuthProvider>
                  </RetornoAdsProvider>
                </SessionProvider>
              </MuiPickersUtilsProvider>
            </TrackingProvider>
          </EmotionThemeProvider>
        </MuiThemeProvider>

        <Script
          id="crisp-id"
          async
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
            window.$crisp=[];window.CRISP_WEBSITE_ID="3d4dd5fa-997e-4fa6-9c88-367d4156862f";
            (function(){d=document;s=d.createElement("script");
            s.src="https://client.crisp.chat/l.js";
            s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
          `,
          }}
        />

        <Script
          id="getsitecontrol-id"
          async
          strategy="lazyOnload"
          src="//l.getsitecontrol.com/pwp1j8wz.js"
        />

        <Script
          strategy="afterInteractive"
          id="active-campaign"
          dangerouslySetInnerHTML={{
            __html: `
            (function(e,t,o,n,p,r,i){
              e.visitorGlobalObjectAlias=n;
              e[e.visitorGlobalObjectAlias]=e[e.visitorGlobalObjectAlias] || function(){
                (e[e.visitorGlobalObjectAlias].q=e[e.visitorGlobalObjectAlias].q||[]).push(arguments)
              };
              e[e.visitorGlobalObjectAlias].l=(new Date).getTime();
              r=t.createElement("script");
              r.src=o;
              r.async=true;
              i=t.getElementsByTagName("script")[0];
              i.parentNode.insertBefore(r,i)
            })(window,document,"https://diffuser-cdn.app-us1.com/diffuser/diffuser.js","vgo");
            vgo('setAccount', '1000732188');
            vgo('setTrackByDefault', true);
            vgo('process');
          `,
          }}
        />
      </CacheProvider>
    </StyledEngineProvider>
  );
}

export default MyApp;
